'use strict';

import angular from 'angular';
import photoResourceModule from '../resources/photo_resource';

const maxPhotos = 5;
const errors = {
  'min-image-dimensions-320x320': 'DIMENSIONS',
  'invalid-image-format': 'FORMAT'
};

class PhotoModel {
  constructor(photoResource, userModel, settings) {
    'ngInject';

    this.photoResource = photoResource;
    this.userModel = userModel;
    this.settings = settings;

    this.collection = [];
  }

  initCollection(photos) {
    this.collection = [].concat(photos || []);
    this._updateCollectionData();
    this._sortCollection();
  }

  getValidPhotos() {
    return this.collection
      .filter((photo) => !photo.$error);
  }

  getByPhotoId(photoId) {
    return this.collection
      .filter(photo => photo.id === photoId)[0];
  }

  uploadFromSocialNetwork(socialNetwork, photoData) {
    const photo = {
      social: {
        network: socialNetwork,
        albumId: photoData.albumId,
        photoId: photoData.id,
        url: photoData.src
      }
    };

    photo.$isUploading = true;
    this.collection.push(photo);

    this._updateCollectionData();

    this.photoResource.uploadFromSocialNetwork(photo)
      .then(photoData => {
        if (this._hasMainPhoto()) {
          delete photoData.main;
        }
        angular.extend(photo, photoData);
      }).catch(response => {
        const responseData = response.data || {};

        if (errors[responseData.error]) {
          photo.$error = errors[responseData.error];
        } else {
          photo.$error = 'DEFAULT';
        }
      }).finally(() => {
        delete photo.$isUploading;
        this._updateCollectionData();
        this._updateMainPhoto();
        this._updateSettingsModel();
      });
  }

  makeMain(photo) {
    return this.photoResource.makeMain(photo.id)
      .then(() => {
        const mainPhoto = this.collection.filter(photo => photo.main)[0];
        if (mainPhoto) {
          delete mainPhoto.main;
        }

        photo.main = true;

        this._sortCollection();
        this._updateMainPhoto();
      });
  }

  rotatePhoto(photo, angle) {
    const postData = {
      id: photo.id,
      url: photo.url
    };
    let crop;
    let width;
    let height;

    if ([90, 270].indexOf(angle) === -1) {
      width = photo.width;
      height = photo.height;
    } else {
      width = photo.height;
      height = photo.width;
    }

    if (width > height) {

      // Landscape photo
      crop = {
        x1: Math.floor((width - height) / 2),
        y1: 0,
        y2: height
      };
      crop.x2 = crop.x1 + height;
    } else {

      // Portrait photo
      crop = {
        x1: 0,
        y1: 0,
        x2: width,
        y2: width
      };
    }

    postData.angle = angle;
    postData.crop = crop;

    return this.photoResource.rotatePhoto(postData)
      .then(() => {
        photo.angle = angle;
        photo.crop = postData.crop;
        this._updateMainPhoto();
      });
  }

  cropPhoto(photo, cropData) {
    const postData = {
      id: photo.id,
      url: photo.url,
      crop: cropData
    };

    return this.photoResource.cropPhoto(postData)
      .then((newPhotoData) => {
        angular.extend(photo, newPhotoData);
        this._updateMainPhoto();
      });
  }

  deletePhoto(photo) {
    this.collection.splice(this.collection.indexOf(photo), 1);
    this._updateCollectionData();
    this._updateSettingsModel();

    return this.photoResource.deletePhoto(photo.id);
  }

  handleUpload(promises) {
    promises.forEach(promise => {
      const photo = {
        $isUploading: true
      };

      this.collection.push(photo);
      this._updateCollectionData();

      promise
        .then(photoData => {
          if (this._hasMainPhoto()) {
            delete photoData.main;
          }
          angular.extend(photo, photoData);
          this._updateMainPhoto();
        })
        .catch(response => {
          const responseData = response.data || {};

          if (errors[responseData.error]) {
            photo.$error = errors[responseData.error];
          } else {
            photo.$error = 'DEFAULT';
          }
        })
        .finally(() => {
          delete photo.$isUploading;
          this._updateCollectionData();
          this._updateSettingsModel();
        });
    });
  }

  _getValidPhotoCount() {
    return this.getValidPhotos().length;
  }

  _updateCollectionData() {
    this._updateHasPhotos();
    this._updatePhotosLeft();
  }

  _updateHasPhotos() {
    this.hasPhotos = Boolean(this._getValidPhotoCount());
  }

  _updatePhotosLeft() {
    this.photosLeft = maxPhotos - this._getValidPhotoCount();
  }

  _sortCollection() {
    this.collection.sort((a, b) => Boolean(b.main) - Boolean(a.main));
  }

  _updateMainPhoto() {
    this.userModel.item.profilePhoto = this.collection.filter(photo => photo.main)[0];
  }

  _updateSettingsModel() {
    this.settings.data.photos.value = this.getValidPhotos().filter(photo => !photo.$isUploading);
  }

  _hasMainPhoto() {
    return Boolean(this.collection.filter(photo => photo.main).length);
  }
}

const dependencies = [
  photoResourceModule
];

export default angular
  .module('models.photoModel', dependencies)
  .service('photoModel', PhotoModel)
  .name;



// WEBPACK FOOTER //
// ./app/src/models/photo_model.js